import type { VerificationQuestionView } from "@credit-acceptance/step6-bff-client";
import { type QueryClient, queryOptions } from "@tanstack/react-query";

import { workbenchResidenceVerificationApi } from "../../../../../api/client";
import { callScriptResidenceKeys } from "./callScriptResidenceKeys";
import {
  ALL_RESIDENCE_CALL_SCRIPT_QUESTION_TYPES,
  resolveResidenceVerificationQuestionType,
} from "./callScriptResidenceQuestions";
import { CALL_SCRIPT_QUESTIONS_STALE_TIME, normalizeVerificationQuestions } from "./useCallScriptQuestions";

/**
 * Shared queryOptions for the residence Call Script verification question
 * catalog (key + queryFn). Backed by
 * WorkbenchResidenceVerificationApi.getResidenceVerificationQuestions
 * (GET /api/v1/workbench/residence-verification/questions?type=), a global
 * lookup keyed only by `type`. The residence counterpart of
 * `callScriptQuestionsQuery`, with the same caching: held fresh for an hour
 * and persisted to localStorage (see AppProviders).
 * @param {string} type - The verification-question type code (`CVDQ` / `CVSSQ`).
 * @param {boolean} enabled - Caller opt-in; combined with the presence guard.
 * @returns {ReturnType<typeof queryOptions>} Typed options object.
 */
export function callScriptResidenceQuestionsQuery(type: string, enabled = true) {
  return queryOptions({
    queryKey: callScriptResidenceKeys.questions(type),
    queryFn: async ({ signal }): Promise<VerificationQuestionView[]> => {
      const response = await workbenchResidenceVerificationApi.getResidenceVerificationQuestions(type, { signal });
      return normalizeVerificationQuestions(response.data);
    },
    staleTime: CALL_SCRIPT_QUESTIONS_STALE_TIME,
    gcTime: 24 * 60 * 60_000,
    retry: false,
    enabled: enabled && !!type,
  });
}

/**
 * Prefetches the residence question catalog for both roles, so Save is
 * normally served from cache. Fire-and-forget: failures are left for
 * {@link ensureCallScriptResidenceQuestionsForPerson} to retry and surface
 * at Save time.
 * @param {QueryClient} queryClient - The app's TanStack Query client.
 * @returns {void}
 */
export function prefetchAllCallScriptResidenceQuestions(queryClient: QueryClient): void {
  for (const type of ALL_RESIDENCE_CALL_SCRIPT_QUESTION_TYPES) {
    void queryClient.prefetchQuery(callScriptResidenceQuestionsQuery(type));
  }
}

/**
 * Resolves a role's residence question catalog, fetching it if the
 * modal-open prefetch hasn't landed yet — called at Save time so a fast
 * Save can't race the prefetch and post an empty `verificationAnswers[]`
 * (same reasoning as `ensureCallScriptQuestionsForPerson`).
 * @param {QueryClient} queryClient - The app's TanStack Query client.
 * @param {"driver" | "secondSigner"} personPath - Which applicant.
 * @returns {Promise<VerificationQuestionView[]>} The role's catalog.
 */
export function ensureCallScriptResidenceQuestionsForPerson(
  queryClient: QueryClient,
  personPath: "driver" | "secondSigner"
): Promise<VerificationQuestionView[]> {
  return queryClient.ensureQueryData(
    callScriptResidenceQuestionsQuery(resolveResidenceVerificationQuestionType(personPath))
  );
}
